import { TECHNO_COLORS } from "../../theme";
import { TECHNO_BPM } from "./technoBeat";

const BEAT_S = 60 / TECHNO_BPM;
const { cyan, magenta, violet, acid } = TECHNO_COLORS;

// Rows run through one colour cycle every four beats. Each row starts half a beat later than
// the one above it, so the colour visibly travels down the grid in time with the kick.
const WAVE_ROWS = 8;
const waveDelays = Object.fromEntries(
  Array.from({ length: WAVE_ROWS }, (_, i) => [
    `body .MuiDataGrid-row:nth-of-type(${WAVE_ROWS}n+${i + 1})`,
    { animationDelay: `${-(WAVE_ROWS - i) * (BEAT_S / 2)}s` }
  ])
);

/**
 * Techno mode for the MUI X data grids: the travelling row wave, a rainbow sweep on the hovered
 * row, a running rainbow line under the column headers, and status dots and chips that pulse
 * on the beat. Merged into the techno global styles.
 */
export const technoGridStyles = {
  "@keyframes techno-row-wave": {
    "0%": { backgroundColor: "rgba(255, 43, 214, 0.2)" },
    "25%": { backgroundColor: "rgba(255, 43, 214, 0.02)" },
    "50%": { backgroundColor: "rgba(0, 240, 255, 0.16)" },
    "75%": { backgroundColor: "rgba(139, 61, 255, 0.04)" },
    "100%": { backgroundColor: "rgba(255, 43, 214, 0.2)" }
  },
  "@keyframes techno-rainbow": {
    "0%": { backgroundPosition: "0% 50%" },
    "100%": { backgroundPosition: "300% 50%" }
  },
  "@keyframes techno-dot": {
    "0%": {
      transform: "scale(1.3)",
      filter: "drop-shadow(0 0 5px rgba(255, 255, 255, 0.95))"
    },
    "100%": {
      transform: "scale(1)",
      filter: "drop-shadow(0 0 1px rgba(255, 255, 255, 0.3))"
    }
  },
  "@keyframes techno-chip": {
    "0%": { boxShadow: `0 0 14px 1px currentColor` },
    "100%": { boxShadow: `0 0 2px 0 currentColor` }
  },
  "body .MuiDataGrid-row": {
    animation: `techno-row-wave ${BEAT_S * 4}s linear infinite`
  },
  ...waveDelays,
  "body .MuiDataGrid-row:hover": {
    animation: "techno-rainbow 1.4s linear infinite",
    // No acid green here: over the purple rows it mixes to olive.
    backgroundImage: `linear-gradient(90deg, rgba(255, 43, 214, 0.45), rgba(0, 240, 255, 0.4), rgba(139, 61, 255, 0.45), rgba(255, 43, 214, 0.45))`,
    backgroundSize: "300% 100%",
    boxShadow: `inset 3px 0 0 ${magenta}, 0 0 18px rgba(255, 43, 214, 0.35)`
  },
  "body .MuiDataGrid-row.Mui-selected": {
    boxShadow: `inset 3px 0 0 ${cyan}`
  },
  "body .MuiDataGrid-columnHeaders": {
    position: "relative"
  },
  "body .MuiDataGrid-columnHeaders::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 2,
    zIndex: 3,
    pointerEvents: "none",
    backgroundImage: `linear-gradient(90deg, ${magenta}, ${cyan}, ${acid}, ${violet}, ${magenta})`,
    backgroundSize: "300% 100%",
    animation: "techno-rainbow 3s linear infinite",
    boxShadow: `0 0 10px ${magenta}`
  },
  "body .MuiDataGrid-cell [data-testid='CircleIcon']": {
    animation: `techno-dot ${BEAT_S}s ease-out infinite`
  },
  "body .MuiDataGrid-cell .MuiChip-root": {
    animation: `techno-chip ${BEAT_S}s ease-out infinite`
  }
};

/** Everything above that moves, for the prefers-reduced-motion block. */
export const technoGridMotionSelectors = [
  "body .MuiDataGrid-row",
  "body .MuiDataGrid-columnHeaders::after",
  "body .MuiDataGrid-cell [data-testid='CircleIcon']",
  "body .MuiDataGrid-cell .MuiChip-root"
].join(", ");
